import { useState } from "react";
import uploadIcon from "@assets/icons/upload.svg";
import trashIcon from "@assets/icons/trash.svg";
import tickIcon from "@assets/icons/tick-circle-filled.svg";

interface FileUploadProps {
  label: string;
  accept?: string;
  description?: string;
  maxSize?: number; // in MB
  onChange?: (file: File | null) => void;
  size?: "compact" | "large"; // Size variant
  helperText?: string; // Helper text for large variant
}

export default function FileUpload({
  label,
  accept = ".pdf,.jpg,.jpeg,.png",
  maxSize = 5,
  onChange,
  description,
  size = "compact",
  helperText,
}: FileUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError("");

    if (file) {
      // Check file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSize) {
        setError(`File size must be less than ${maxSize}MB`);
        return;
      }

      setSelectedFile(file);
      onChange?.(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
    onChange?.(null);
  };

  return (
    <div className="w-full">
      <label className="text-[16px] md:text-[18px] lg:text-xl text-[#0A0E2EBF] font-medium block mb-2 md:mb-3">
        {label}
      </label>

      {!selectedFile ? (
        <div
          className={`bg-[#F1F1F1] border-[1px] border-dashed border-[#62636C] rounded-[16px] w-full grid place-content-center relative hover:border-[#F03813] transition-colors ${
            size === "large"
              ? "h-[150px] md:h-[173px] max-w-full"
              : "h-[80px] max-w-[554px]"
          }`}
        >
          <input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          {size === "large" ? (
            <div className="text-center px-4">
              <img
                src={uploadIcon}
                alt="Upload"
                className="w-8 h-8 md:w-10 md:h-10 mx-auto"
              />
              <span className="text-[#62636C] text-[14px] md:text-[16px] lg:text-lg block text-center mt-4 md:mt-6">
                {helperText || description}
              </span>
            </div>
          ) : (
            <div className="text-center flex-col lg:flex-row flex items-center justify-center  lg:gap-[24px]">
              <img
                src={uploadIcon}
                alt="Upload"
                className="w-5 h-5 md:w-6 md:h-6"
              />
              <span className="text-[#62636C] text-[14px] md:text-[16px] lg:text-lg">
                {description}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`bg-[#F1F1F1] border-[1px] border-dashed border-[#62636C] rounded-[16px] w-full place-content-center relative hover:border-[#F03813] transition-colors flex items-center justify-between px-4 md:px-6 ${
            size === "large"
              ? "h-[150px] md:h-[173px] max-w-full"
              : "h-[80px] max-w-[554px]"
          }`}
        >
          <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
            {/* File Info */}
            <div className="flex-1 flex items-center gap-[12px] min-w-0">
              <p className="text-[14px] md:text-[24px] text-[#62636C] font-normal truncate">
                {selectedFile.name}
              </p>
              {/* Check Mark Icon */}
              <div className="flex-shrink">
                <img src={tickIcon} alt="" />
              </div>
            </div>
          </div>

          {/* Delete Button */}
          <button
            onClick={handleRemoveFile}
            className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full hover:bg-[#FEF6F4] flex items-center justify-center transition-colors group"
            type="button"
          >
            <img src={trashIcon} alt="" />
          </button>
        </div>
      )}

      {error && (
        <p className="text-[#F03813] text-[12px] md:text-[14px] mt-2">
          {error}
        </p>
      )}
    </div>
  );
}
